import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Home as HomeIcon, Building2, Calculator, FileText, Calendar, Eye, ArrowRight, BookOpen, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [articles, setArticles] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchArticles();
    checkAdminStatus();
    
    // Scroll vers la section blog si hash #blog
    if (location.hash === '#blog') {
      setTimeout(() => {
        const blogSection = document.getElementById('blog');
        if (blogSection) {
          blogSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, [location]);

  const checkAdminStatus = () => {
    const token = localStorage.getItem('admin_token');
    setIsAdmin(!!token);
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles`, { params: { limit: 12 } });
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Erreur chargement articles:', error);
    } finally {
      setLoadingBlog(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Bouton Admin flottant si connecté */}
      {isAdmin && (
        <div className="fixed top-4 right-4 z-50">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg hover:opacity-90 transition-all"
            style={{ background: '#335C2E', color: '#FFFFFF' }}
          >
            <Settings size={18} />
            Espace Admin
          </button>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F0EDE6 50%, #E8E4D9 100%)'
      }}>
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-32">
          <div className="text-center space-y-8">
            <div className="inline-block">
              <img 
                src="/logo-refacingvr.svg" 
                alt="Refacing VR" 
                className="h-24 sm:h-32 mx-auto mb-4"
                style={{ maxWidth: '400px' }}
              />
            </div>
            <p className="text-lg sm:text-xl max-w-3xl mx-auto" style={{ color: '#5A5A5A' }}>
              Transformez votre vision en réalité avec nos pavillons sur mesure.
              Obtenez une estimation détaillée et un précontrat en quelques minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <button
                onClick={() => navigate('/estimate')}
                className="btn-primary text-lg px-10 py-4"
                data-testid="estimate-project-btn"
              >
                <Calculator className="inline-block mr-2 w-5 h-5" />
                Estimer mon projet
              </button>
            </div>
          </div>
        </div>
        
        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48L60 56C120 64 240 80 360 77.3C480 74.7 600 53.3 720 48C840 42.7 960 53.3 1080 58.7C1200 64 1320 64 1380 64H1440V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V48Z" fill="#FAFAFA"/>
          </svg>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16" style={{ color: '#335C2E' }}>
          Comment ça fonctionne
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="card text-center hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)' }}>
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#333333' }}>1. Remplissez le formulaire</h3>
            <p style={{ color: '#666666' }}>
              Fournissez vos coordonnées et les détails de votre projet en quelques étapes simples.
            </p>
          </div>

          <div className="card text-center hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C7A027 0%, #D4B23A 100%)' }}>
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#333333' }}>2. Obtenez votre estimation</h3>
            <p style={{ color: '#666666' }}>
              Calculez instantanément le prix selon votre modèle, surface et options choisies.
            </p>
          </div>

          <div className="card text-center hover:shadow-xl transition-shadow duration-300">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)' }}>
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#333333' }}>3. Recevez votre précontrat</h3>
            <p style={{ color: '#666666' }}>
              Téléchargez votre PDF avec les détails complets, l'échéancier et le calendrier.
            </p>
          </div>
        </div>
      </div>

      {/* Models Preview */}
      <div className="py-20" style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12" style={{ color: '#335C2E' }}>
            Nos modèles
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#C7A027' }}>Maisons de parc</h3>
              <ul className="space-y-2" style={{ color: '#666666' }}>
                <li>• Modèle 12×36</li>
                <li>• Modèle 12×40</li>
              </ul>
              <p className="mt-4 text-sm" style={{ color: '#888888' }}>Parfait pour créer un espace de vie confortable et élégant.</p>
            </div>
            <div className="card">
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#C7A027' }}>UHA (Unités Habitables Accessoires)</h3>
              <ul className="space-y-2" style={{ color: '#666666' }}>
                <li>• Studio 12×24</li>
                <li>• 1 chambre 12×28</li>
                <li>• 2 chambres 12×32</li>
              </ul>
              <p className="mt-4 text-sm" style={{ color: '#888888' }}>Idéal pour un revenu locatif ou loger la famille.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Section */}
      <div id="blog" className="py-20 scroll-mt-20" style={{ background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold" style={{ color: '#335C2E' }}>
                <BookOpen className="inline-block mr-3 mb-1" size={36} />
                Notre Blog
              </h2>
              <p className="mt-2 text-lg" style={{ color: '#666666' }}>
                Découvrez nos conseils, actualités et inspirations
              </p>
            </div>
            {/* Bouton supprimé car tous les articles sont déjà affichés */}
          </div>

          {loadingBlog ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
              <p className="mt-4" style={{ color: '#666666' }}>Chargement des articles...</p>
            </div>
          ) : articles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <div
                    key={article.id}
                    onClick={() => navigate(`/blog/${article.slug}`)}
                    className="rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    style={{ background: '#FAFAFA' }}
                  >
                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-bold mb-2 line-clamp-2 hover:underline" style={{ color: '#335C2E' }}>
                        {article.title}
                      </h3>
                      <p className="text-sm mb-4 line-clamp-2" style={{ color: '#666666' }}>
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs" style={{ color: '#999999' }}>
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(article.published_at || article.created_at).toLocaleDateString('fr-CA')}
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye size={14} />
                          {article.views} vues
                        </div>
                      </div>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {article.tags.slice(0, 2).map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 rounded text-xs"
                              style={{ background: '#E8F5E9', color: '#2E7D32' }}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 rounded-lg" style={{ background: '#F5F5F5' }}>
              <BookOpen size={48} className="mx-auto mb-4" style={{ color: '#999999' }} />
              <p style={{ color: '#666666' }}>Aucun article publié pour le moment</p>
              <p className="text-sm mt-2" style={{ color: '#999999' }}>Revenez bientôt pour découvrir nos actualités !</p>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16" style={{ background: 'linear-gradient(135deg, #335C2E 0%, #4A7D42 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Prêt à concrétiser votre projet ?
          </h2>
          <p className="text-lg text-white opacity-90 mb-8">
            Obtenez votre estimation gratuite en quelques minutes
          </p>
          <button
            onClick={() => navigate('/estimate')}
            className="px-10 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
            style={{ background: '#C7A027', color: '#FFFFFF' }}
          >
            <Calculator className="inline-block mr-2 w-5 h-5" />
            Commencer mon estimation
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12" style={{ background: '#2A4A24', color: 'white' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Contact */}
            <div className="text-center md:text-left">
              <p className="text-lg font-semibold mb-2">Refacing VR - Division Pavillons</p>
              <p className="text-sm opacity-90">info@refacingvr.com</p>
            </div>
            
            {/* Legal Links */}
            <div className="text-center">
              <p className="font-semibold mb-3">Informations légales</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/legal/privacy_policy')}
                  className="text-sm opacity-80 hover:opacity-100 hover:underline"
                >
                  Politique de confidentialité
                </button>
                <button
                  onClick={() => navigate('/legal/terms_of_service')}
                  className="text-sm opacity-80 hover:opacity-100 hover:underline"
                >
                  Conditions d'utilisation
                </button>
                <button
                  onClick={() => navigate('/data-deletion')}
                  className="text-sm opacity-80 hover:opacity-100 hover:underline"
                >
                  Supprimer mes données
                </button>
              </div>
            </div>
            
            {/* Quick links */}
            <div className="text-center md:text-right">
              <p className="font-semibold mb-3">Liens rapides</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => navigate('/estimate')}
                  className="text-sm opacity-80 hover:opacity-100 hover:underline"
                >
                  Demander un devis
                </button>
                <button
                  onClick={() => {
                    const blogSection = document.getElementById('blog');
                    if (blogSection) {
                      blogSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-sm opacity-80 hover:opacity-100 hover:underline"
                >
                  Blog
                </button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/20 pt-6 text-center">
            <p className="text-xs opacity-75">
              © 2025 Refacing VR. Tous droits réservés.
              {/* Lien caché pour les admins - invisible pour les utilisateurs normaux */}
              <span 
                onClick={() => navigate('/login')} 
                className="cursor-pointer opacity-0 hover:opacity-100 transition-opacity ml-2"
                title="Admin"
              >
                •
              </span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
